"use client";

import { useEffect, useState } from "react";

interface ErrorProps {
	error: Error & { digest?: string };
	retry: () => void;
}

export default function ErrorPage({ error, retry }: ErrorProps) {
	const [isRedirecting, setIsRedirecting] = useState(false);

	useEffect(() => {
		const msg = typeof error.message === "string" ? error.message : "";
		if (msg.startsWith("REDIRECT:")) {
			setIsRedirecting(true);
		}
	}, [error]);

	useEffect(() => {
		// Log the error to an error reporting service
		console.error("App error:", error);
	}, [error]);

	if (isRedirecting) {
		const msg = typeof error.message === "string" ? error.message : "";
		window.location.href = msg.replace("REDIRECT:", "");

		return (
			<div className="flex items-center justify-center py-12">
				<span className="text-sm text-zinc-400 dark:text-zinc-500">
					Redirecting…
				</span>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-zinc-50 font-sans dark:bg-background flex items-center justify-center">
			<div className="max-w-md w-full space-y-8">
				<div className="text-center">
					<h2 className="text-page-title font-normal tracking-[-0.025em] text-gray-900 dark:text-white">
						Something went wrong!
					</h2>
					<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
						{typeof error.message === "string"
							? error.message
							: "An unexpected error occurred."}
					</p>
					<div className="mt-6">
						<button
							type="button"
							onClick={() => {
								// A failed script stays failed in the browser's module cache.
								// Reload the document to pick up the current build's chunks.
								if (
									/ChunkLoadError|Failed to load chunk|Loading chunk .+ failed|Failed to fetch dynamically imported module/i.test(
										`${error.name}: ${error.message}`,
									)
								) {
									window.location.reload();
								} else retry();
							}}
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
						>
							Try again
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
